import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8">
        {sent ? (
          <div className="text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h1 className="text-2xl font-display font-bold text-slate-900">Check your inbox</h1>
            <p className="text-sm text-slate-600 mt-2" data-testid="forgot-password-sent-message">
              If an account exists for <span className="font-mono">{email}</span>, a reset link is on its way.
            </p>
            <Link to="/login"><Button variant="outline" className="mt-6">Back to sign in</Button></Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-display font-bold tracking-tight text-slate-900">Forgot password?</h1>
            <p className="mt-1 text-sm text-slate-600">We'll email you a link to reset it.</p>
            {err && <Alert variant="destructive" className="mt-4"><AlertDescription>{err}</AlertDescription></Alert>}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email-input" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-password-submit-button">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <div className="mt-6 text-sm text-slate-600">
              Remember your password? <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
